import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { getCategoriesData } from '../Home/categoriesData';

const AppLauncher = ({ features = [] }) => {
  const intl = useIntl();
  // Memoize categories data to avoid recalculating on every render
  const categoriesData = useMemo(() => getCategoriesData(features), [features]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  // Install a CSS override so tour tooltip always sits above the launcher when the tour is open
  useEffect(() => {
    try {
      const STYLE_ID = 'app-launcher-tour-override';
      if (!document.getElementById(STYLE_ID)) {
        const el = document.createElement('style');
        el.id = STYLE_ID;
        el.textContent = 'body.guided-tour-open [data-app-launcher-panel="1"]{ z-index: 1900 !important; }';
        document.head.appendChild(el);
      }
    } catch (_) {}
  }, []);
  // Debug helpers to toggle from console if needed
  useEffect(() => {
    try {
      window.__openAppLauncher = () => setOpen(true);
      window.__closeAppLauncher = () => setOpen(false);
      window.__toggleAppLauncher = () => setOpen((v) => !v);
    } catch (_) {}
    return () => {
      try {
        delete window.__openAppLauncher;
        delete window.__closeAppLauncher;
        delete window.__toggleAppLauncher;
      } catch (_) {}
    };
  }, []);
  // Keep it simple and robust: close when clicking anywhere outside
  useEffect(() => {
    const onDocMouseDown = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open]);

  const gridIcon = (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
    </svg>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="p-2 rounded-md text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-secondary"
        aria-haspopup="true"
        aria-expanded={open}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="All Tools"
        data-tutorial-id="app-launcher-button"
      >
        {gridIcon}
      </button>

      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        (
          <div
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            data-app-launcher-panel="1"
            className="bg-succession-bg-secondary border border-succession-border rounded-xl shadow-succession-lg p-4 max-h-[70vh] overflow-auto"
            style={{ position: 'fixed', top: 64, right: 24, width: '760px', maxWidth: '92vw', zIndex: 2147483647, display: 'block', visibility: 'visible' }}
          >
            <div data-tutorial-id="app-launcher-panel" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              {(categoriesData || []).map((cat) => (
                <div key={`app-launch-${cat.id}`} className="">
                  <div className="text-sm font-semibold mb-2 text-succession-text-primary">
                    {cat.labelKey ? intl.formatMessage({ id: cat.labelKey, defaultMessage: cat.label }) : cat.label}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(cat.tools || []).map((tool) => (
                      <a
                        key={`${cat.id}-${tool.id || tool.label}`}
                        href={tool.route || tool.href || '#'}
                        className="flex items-center gap-2 p-2 rounded-md bg-succession-bg-primary border border-succession-border hover:bg-succession-hover-bg text-succession-text-secondary hover:text-succession-text-primary text-xs"
                      >
                        {tool.icon ? (<tool.icon className="w-4 h-4 flex-shrink-0" />) : (<span className="w-4 h-4" />)}
                        <span className="font-medium truncate">
                          {tool.labelKey ? intl.formatMessage({ id: tool.labelKey, defaultMessage: tool.label }) : tool.label}
                        </span>
                        {tool.comingSoon && <span className="ml-auto text-[10px] text-gray-400">{intl.formatMessage({ id: 'succession.sidebar.soon', defaultMessage: 'Soon' })}</span>}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default AppLauncher;


